import React, { useState, useMemo } from "react";
import { withRouter } from "react-router-dom";
import queries from "../helpers/queries";
import { useForm } from "react-hook-form";
import { withUser } from "../Contexts/user";
import Table from "../Components/darkBasicTable";
import { useQuery } from "@apollo/react-hooks";
import useDimensions from "../Hooks/useDimensions";
import Select from "react-select";
import _ from "lodash";
import excel from "../Components/excel";

const capitalizeWords = str => {
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

const statusOptions = [
    {
        label: "All",
        value: "all",
    },
    {
        label: "Active",
        value: "active",
    },
    {
        label: "Risk Suspended",
        value: "risksuspended",
    },
    {
        label: "Suspended",
        value: "suspended",
    },
];

const rankings = [
    {
        label: "All",
        value: "all",
    },
    {
        label: "Prime Leader",
        value: "5ec264312bde7cefbf119c4c",
    },
    {
        label: "Leader",
        value: "5ec2676c877421f075d0cbff",
    },
    {
        label: "Agent",
        value: "5ec5f937dfa89a2ccea9e9d9",
    },
];

const RiskyDownlines = withRouter(() => {
    const size = useDimensions();

    const {
        handleSubmit: handleFilterSubmit,
        reset,
        errors,
    } = useForm({
        defaultValues: {
            name: "",
            referral: null,
        },
    });

    const filterSelectStyles = useMemo(
        () => ({
            control: (provided, state) => ({
                ...provided,
                borderColor: errors.area ? "#fc8181" : "#e2e8f0",
                backgroundColor: "#fbfdfe",
                borderRadius: "0.5rem",
                padding: "0.20rem",
                "&:hover": {
                    borderColor: errors.area ? "#fc8181" : "#e2e8f0",
                },
            }),

            placeholder: (provided, state) => ({
                ...provided,
                color: "#a0aec0",
                fontSize: "0.75rem",
                fontWeight: "600",
            }),

            container: (provided, state) => ({
                ...provided,
                color: "#64748b",
                fontSize: "0.75rem",
                fontWeight: "600",
                width: "100%",
            }),
        }),
        [errors]
    );

    const [filters, setFilters] = useState({
        name: undefined,
        referral: undefined,
        status: "active",
        rankings: "all",
    });

    const updateFilters = (values) => {
        setFilters((prev) => ({
            ...prev,
            ...values,
            name: values.name !== "" ? values.name.value : filters.name,
            referral:
                values.referral !== null
                    ? values.referral.value
                    : filters.referral,
            status:
                values.status !== undefined
                    ? values.status.value
                    : filters.status,
            rankings:
                values.rankings !== undefined
                    ? values.rankings.value
                    : filters.rankings,
        }));

        setPaginationState((prev) => ({ ...prev, page: 1 }));
    };

    const columns = useMemo(() => {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        if (size.width > 650) {
            return [
                {
                    Header: "Name",
                    accessor: "name", 
                    Cell: ({ row: { original } }) => (
                        <div>
                            {capitalizeWords(`${original.nickname != '-' ? `${original.nickname} ` : ''}${original.name}`)}
                        </div>
                    ),
                },
                {
                    Header: "Ranking",
                    accessor: "progression",
                    Cell: ({ value }) => (
                        <div className="capitalize">{value}</div>
                    ),
                },
                {
                    Header: "No Case",
                    accessor: "risk",
                    Cell: ({ value }) => (
                        <div className="capitalize">
                            {value === "lv1"
                                ? "1 Week"
                                : value === "lv0"
                                ? "New"
                                : value === "lv2"
                                ? "2 Weeks"
                                : value === "lv3"
                                ? "3 Weeks"
                                : value === "lv4"
                                ? "1 Month"
                                : value === "lv5"
                                ? "2 Months"
                                : value === "lv6"
                                ? "3 Months"
                                : "clear"}
                        </div>
                    ),
                },
                {
                    Header: "No Convert",
                    accessor: "noConvert",
                    Cell: ({ value }) => (
                        <div className="capitalize">
                            {value === "lv1"
                                ? "1 Week"
                                : value === "lv0"
                                ? "New"
                                : value === "lv2"
                                ? "2 Weeks"
                                : value === "lv3"
                                ? "3 Weeks"
                                : value === "lv4"
                                ? "1 Month"
                                : value === "lv5"
                                ? "2 Months"
                                : value === "lv6"
                                ? "3 Months"
                                : "clear"}
                        </div>
                    ),
                },
                {
                    Header: "Upline",
                    accessor: "referral", 
                    Cell: ({ row: { original } }) => (
                        <div>
                            {original.referral ? capitalizeWords(`${original.referral.nickname != '-' ? `${original.referral.nickname} ` : ''}${original.referral.name}`) : ''}
                        </div>
                    ),
                },
                {
                    Header: "Second Upline",
                    accessor: "secondReferral", 
                    Cell: ({ row: { original } }) => (
                        <div>
                            {original.secondReferral ? capitalizeWords(`${original.secondReferral.nickname != '-' ? `${original.secondReferral.nickname} ` : ''}${original.secondReferral.name}`) : ''}
                        </div>
                    ),
                },
            ];
        } else {
            return [
                {
                    Header: "Name",
                    accessor: "name", 
                    Cell: ({ row: { original } }) => (
                        <div>
                            {capitalizeWords(`${original.nickname != '-' ? `${original.nickname} ` : ''}${original.name}`)}
                        </div>
                    ),
                },
                {
                    Header: "Personal Sales",
                    accessor: "personalSales",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group Sales",
                    accessor: "groupSales",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)}</div>
                    ),
                },
            ];
        }
    }, [size]);

    const [paginationState, setPaginationState] = useState({
        limit: 10,
        page: 1,
    });

    const { loading: downlinesLoading, data: downlinesData } = useQuery(
        queries.FETCH_RISKY_DOWNLINES,
        {
            variables: {
                limit: paginationState.limit,
                page: paginationState.page,
                name: filters.name,
                upline: filters.referral,
                status: filters.status,
                rank: filters.rankings,
            },
            fetchPolicy: "network-only",
            onCompleted: ({ fetchRiskyDownlines: { pagination } }) =>
                setPaginationState(pagination),
        }
    );

    return (
        <div
            className="min-h-screen bg-cover"
            style={{
                backgroundImage:
                    "linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)), url('https://venti-fans.com/wp-content/uploads/2025/03/DD_SystemImages_4pSPeG_vc.webp')",
            }}
        >
            <div className="lg:p-8 p-2 lg:my-0 my-10 h-full">
                <div className="w-full lg:pr-4">
                    <form
                        className="grid grid-cols-1 lg:grid-cols-3 mb-4 col-gap-4 row-gap-4"
                        onSubmit={handleFilterSubmit(updateFilters)}
                    >
                        <div className="">
                            <Select
                                styles={filterSelectStyles}
                                isSearchable={false}
                                options={statusOptions}
                                value={filters.status}
                                placeholder={_.capitalize(filters.status)}
                                onChange={(val) => {
                                    setPaginationState((prev) => ({
                                        ...prev,
                                        page: 1,
                                    }));
                                    setFilters((prev) => ({
                                        ...prev,
                                        status: val.value,
                                    }));
                                }}
                            />
                        </div>
                        <div className="">
                            <Select
                                styles={filterSelectStyles}
                                isSearchable={false}
                                options={rankings}
                                value={filters.rankings}
                                placeholder={
                                    filters.rankings ===
                                    "5ec5f937dfa89a2ccea9e9d9"
                                        ? "Agent"
                                        : filters.rankings ===
                                          "5ec2676c877421f075d0cbff"
                                        ? "Leader"
                                        : filters.rankings === "all"
                                        ? "All"
                                        : "Prime Leader"
                                }
                                onChange={(val) => {
                                    setPaginationState((prev) => ({
                                        ...prev,
                                        page: 1,
                                    }));
                                    setFilters((prev) => ({
                                        ...prev,
                                        rankings: val.value,
                                    }));
                                }}
                            />
                        </div>
                        <div className="flex flex-row justify-start items-center lg:justify-start text-center">
                            <div
                                className="inline bg-icongreen text-white w-1/2 lg:w-1/3 px-4 text-medium border-solid cursor-pointer border border-gray-400 text-sm p-2"
                                onClick={() => {
                                    setFilters((prev) => ({
                                        ...prev,
                                        name: null,
                                        upline: null,
                                    }));
                                    reset();
                                }}
                            >
                                Reset
                            </div>
                            <div
                                className="inline bg-icongreen text-white w-1/2 lg:w-1/3 px-4 text-medium border-solid cursor-pointer border border-gray-400 text-sm p-2 ml-2"
                                onClick={() => {
                                    let payload = [];

                                    if (downlinesData && downlinesData.fetchRiskyDownlines.data) {
                                        downlinesData.fetchRiskyDownlines.data.forEach((user) => {
                                            payload.push({
                                                Name: capitalizeWords(`${(user.nickname && user.nickname !== "-" ? `${user.nickname} ` : ``)}${user.name}`),
                                                Ranking: user.progression,
                                                NoCase: user.risk === "lv1" ? "1 Week" :
                                                       user.risk === "lv0" ? "New" :
                                                       user.risk === "lv2" ? "2 Weeks" :
                                                       user.risk === "lv3" ? "3 Weeks" :
                                                       user.risk === "lv4" ? "1 Month" :
                                                       user.risk === "lv5" ? "2 Months" :
                                                       user.risk === "lv6" ? "3 Months" : "Clear",
                                                NoConvert: user.noConvert === "lv1" ? "1 Week" :
                                                          user.noConvert === "lv0" ? "New" :
                                                          user.noConvert === "lv2" ? "2 Weeks" :
                                                          user.noConvert === "lv3" ? "3 Weeks" :
                                                          user.noConvert === "lv4" ? "1 Month" :
                                                          user.noConvert === "lv5" ? "2 Months" :
                                                          user.noConvert === "lv6" ? "3 Months" : "Clear",
                                                Upline: user.referral ? capitalizeWords(`${(user.referral.nickname && user.referral.nickname !== "-" ? `${user.referral.nickname} ` : ``)}${user.referral.name}`) : "",
                                                SecondUpline: user.secondReferral ? capitalizeWords(`${(user.secondReferral.nickname && user.secondReferral.nickname !== "-" ? `${user.secondReferral.nickname} ` : ``)}${user.secondReferral.name}`) : "",
                                            });
                                        });
                                    }

                                    excel({
                                        excelData: payload,
                                        fileName: `RiskyDownlines-${new Date().toISOString().split('T')[0]}`,
                                    });
                                }}
                            >
                                Export
                            </div>
                        </div>
                    </form>
                </div>
                <div className="mt-6 table-header-actions flex justify-end items-start"></div>
                <Table
                    columns={columns}
                    data={
                        downlinesData
                            ? downlinesData.fetchRiskyDownlines.data
                            : []
                    }
                    updatePagination={setPaginationState}
                    loading={downlinesLoading}
                    pageCount={
                        downlinesData
                            ? downlinesData.fetchRiskyDownlines.pagination
                                  .totalPages
                            : 0
                    }
                    totalCount={
                        downlinesData
                            ? downlinesData.fetchRiskyDownlines.pagination
                                  .documents
                            : 0
                    }
                />
            </div>
        </div>
    );
});

export default withUser(RiskyDownlines);
